import React, { useContext } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Tooltip,
  Chip,
  Box,
  Typography,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ConfiguracionFactura } from './types/facturas.types';
import { DarkModeContext } from '../../../context/darkModeContext';
import './configuracionFacturasTable.scss';

interface Props {
  configuraciones: ConfiguracionFactura[];
  onEdit: (config: ConfiguracionFactura) => void;
}

const ConfiguracionFacturasTable: React.FC<Props> = ({ configuraciones, onEdit }) => {
  const { darkMode } = useContext(DarkModeContext);

  const calcularDisponibles = (config: ConfiguracionFactura) => {
    return config.NumeroFin - config.NumeroActual + 1;
  };

  const getStatusChip = (disponibles: number) => {
    if (disponibles <= 10) {
      return (
        <Chip 
          icon={<WarningIcon />}
          label="Crítico"
          color="error"
          size="small"
          className="status-chip critical"
        />
      );
    } else if (disponibles <= 30) {
      return (
        <Chip 
          icon={<WarningIcon />}
          label="Bajo"
          color="warning"
          size="small"
          className="status-chip warning"
        />
      );
    }
    return (
      <Chip 
        icon={<CheckCircleIcon />}
        label="Disponible"
        color="success"
        size="small"
        className="status-chip success"
      />
    );
  };

  if (configuraciones.length === 0) {
    return (
      <Box className="empty-state">
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No hay configuraciones
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Crea una nueva configuración para empezar
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={`facturas-table-container ${darkMode ? 'dark' : ''}`}>
      <Table>
        <TableHead>
          <TableRow className="table-header">
            <TableCell className="header-cell">Prefijo</TableCell>
            <TableCell className="header-cell" align="center">Número Inicial</TableCell>
            <TableCell className="header-cell" align="center">Número Final</TableCell>
            <TableCell className="header-cell" align="center">Número Actual</TableCell>
            <TableCell className="header-cell" align="center">Disponibles</TableCell>
            <TableCell className="header-cell" align="center">Estado</TableCell>
            <TableCell className="header-cell" align="center">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configuraciones.map((config) => {
            const disponibles = calcularDisponibles(config);
            
            return (
              <TableRow key={config.Id} className="table-row">
                <TableCell className="table-cell">
                  <span className="prefijo-text">
                    {config.Prefijo || 'Sin prefijo'}
                  </span>
                </TableCell>
                <TableCell className="table-cell" align="center">
                  <span className="numero-text">{config.NumeroInicio}</span>
                </TableCell>
                <TableCell className="table-cell" align="center">
                  <span className="numero-text">{config.NumeroFin}</span>
                </TableCell>
                <TableCell className="table-cell" align="center">
                  <span className="numero-actual">{config.NumeroActual}</span>
                </TableCell>
                <TableCell className="table-cell" align="center">
                  <span className={`disponibles-text ${disponibles <= 30 ? 'warning' : ''}`}>
                    {disponibles}
                  </span>
                </TableCell>
                <TableCell className="table-cell" align="center">
                  {getStatusChip(disponibles)}
                </TableCell>
                <TableCell className="table-cell" align="center">
                  <Tooltip title="Editar configuración">
                    <IconButton 
                      onClick={() => onEdit(config)} 
                      color="primary"
                      size="small"
                      className="action-button"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {configuraciones.some(c => calcularDisponibles(c) <= 30) && (
        <Box sx={{ p: 2 }}>
          <Alert 
            severity={configuraciones.some(c => calcularDisponibles(c) <= 10) ? "error" : "warning"}
            className="alert-disponibles"
          >
            Quedan pocas facturas disponibles. Considera actualizar la configuración.
          </Alert>
        </Box>
      )}
    </TableContainer>
  );
};

export default ConfiguracionFacturasTable;